import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import CertificateUploadDialog from './CertificateUploadDialog';
import MarkFailedDialog from './MarkFailedDialog';
import DelegatePicker from './DelegatePicker';
import { addDelegateToBooking, removeDelegateFromBooking } from '@/lib/delegateService';
import {
  User,
  Users,
  UserPlus,
  UserMinus,
  CheckCircle,
  XCircle,
  Clock,
  Upload,
  AlertTriangle,
  FileCheck,
  Loader2,
  MoreVertical,
  Search,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Download,
  ExternalLink
} from 'lucide-react';
import { cn } from '@/lib/utils';

const BookedDelegatesTable = ({ booking, onDelegateChange }) => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [delegates, setDelegates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [confirmFailedOpen, setConfirmFailedOpen] = useState(false);
  const [failedDialogOpen, setFailedDialogOpen] = useState(false);
  const [selectedDelegate, setSelectedDelegate] = useState(null);

  // Add delegates dialog state
  const [addDelegateOpen, setAddDelegateOpen] = useState(false);

  // Pending changes for batch save (only applies to Manage Delegates dialog)
  const [pendingAdditions, setPendingAdditions] = useState([]);
  const [pendingRemovals, setPendingRemovals] = useState(new Set());
  const [savingChanges, setSavingChanges] = useState(false);

  // Remove delegate dialog state
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [removingDelegate, setRemovingDelegate] = useState(false);

  // Sorting state
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Filter state
  const [filters, setFilters] = useState({
    pending: true,
    attended: true,
    absent: true,
    cancelled: true,
    not_started: true,
    completed: true,
    failed: true,
  });

  useEffect(() => {
    if (booking?.id) {
      fetchDelegates();
    }
  }, [booking?.id]);

  const fetchDelegates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('booking_delegates')
        .select(`
          *,
          space_member:space_members!inner (
            id,
            user_id,
            role,
            user_profiles!space_members_user_id_fkey!inner (
              id,
              display_name,
              email,
              avatar_url,
              first_name,
              last_name
            )
          )
        `)
        .eq('training_quote_id', booking.id)
        .order('created_date', { ascending: true });

      if (error) throw error;

      setDelegates(data || []);
    } catch (error) {
      console.error('Error fetching delegates:', error);
      toast.error('Failed to load delegates');
    } finally {
      setLoading(false);
    }
  };

  // Assignment tracking
  const totalSpaces = parseInt(booking?.delegates) || 0;
  const assignedCount = delegates.length;

  // Helper to get display name with fallback to first/last name
  const getDisplayName = (profile) => {
    if (!profile) return null;
    if (profile.display_name) return profile.display_name;
    const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
    return fullName || null;
  };

  // Map delegates to the format DelegatePicker expects
  // This shows the "working state" in the dialog: current + pending additions - pending removals
  const assignedForPicker = useMemo(() => {
    // Start with current delegates (excluding pending removals)
    const currentDelegates = delegates
      .filter(d => !pendingRemovals.has(d.space_member_id))
      .map(d => {
        const profile = d.space_member?.user_profiles;
        return {
          id: d.space_member?.id || d.space_member_id,
          space_member_id: d.space_member_id,
          display_name: getDisplayName(profile),
          name: getDisplayName(profile), // Also set name for DelegatePicker fallback
          email: profile?.email,
          avatar_url: profile?.avatar_url,
          user_id: d.space_member?.user_id,
          user_profiles: profile,
          _bookingDelegateRecord: d
        };
      });

    // Add pending additions
    const pendingDelegates = pendingAdditions.map(member => {
      const profile = member.user_profiles || member.user || {};
      return {
        id: member.id || member.space_member_id,
        space_member_id: member.id || member.space_member_id,
        display_name: profile.display_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
        email: profile.email,
        avatar_url: profile.avatar_url,
        user_id: profile.id || member.user_id,
        user_profiles: profile,
        _isPendingAddition: true,
        _originalMember: member
      };
    });

    return [...currentDelegates, ...pendingDelegates];
  }, [delegates, pendingAdditions, pendingRemovals]);

  const ensureSpaceMember = async (member) => {
    if (!member._isCompanyMember) return member;
    const userId = member.user_id || member.user_profiles?.id;
    if (!userId) return member;

    const { data, error } = await supabase
      .from('space_members')
      .insert({
        project_id: booking.project_id,
        user_id: userId,
        role: 'Member',
        status: 'active',
        show_on_matrix: true,
        last_active: new Date().toISOString(),
        sites: 1,
        created_date: new Date().toISOString()
      })
      .select('id, user_id, role')
      .single();

    if (error) {
      console.warn('Failed to create space member:', error);
      return member;
    }
    return { ...member, id: data.id, space_member_id: data.id, _isCompanyMember: false };
  };

  // Reset pending changes when dialog closes
  const resetPendingChanges = () => {
    setPendingAdditions([]);
    setPendingRemovals(new Set());
  };

  // Handle dialog close - reset pending changes
  const handleDialogClose = (open) => {
    if (!open) {
      resetPendingChanges();
    }
    setAddDelegateOpen(open);
  };

  // Handle adding a delegate (local state only, no database call)
  const handlePendingAdd = async (member) => {
    const spaceMember = await ensureSpaceMember(member);
    const spaceMemberId = spaceMember.id || spaceMember.space_member_id;

    // Check if this member is in pending removals - if so, just remove from removals
    if (pendingRemovals.has(spaceMemberId)) {
      setPendingRemovals(prev => {
        const newSet = new Set(prev);
        newSet.delete(spaceMemberId);
        return newSet;
      });
      return;
    }

    // Check if already in delegates or pending additions
    const alreadyAssigned = delegates.some(d => d.space_member_id === spaceMemberId);
    const alreadyPending = pendingAdditions.some(m => (m.id || m.space_member_id) === spaceMemberId);

    if (alreadyAssigned || alreadyPending) return;

    setPendingAdditions(prev => [...prev, spaceMember]);
  };

  // Handle removing a delegate (local state only, no database call)
  const handlePendingRemove = (delegate) => {
    const spaceMemberId = delegate.space_member_id || delegate.id;

    // If this is a pending addition, just remove from pending
    if (delegate._isPendingAddition) {
      setPendingAdditions(prev => prev.filter(m => (m.id || m.space_member_id) !== spaceMemberId));
      return;
    }

    // Otherwise, mark existing delegate for removal
    setPendingRemovals(prev => new Set([...prev, spaceMemberId]));
  };

  // Save all pending changes to database
  const handleSaveChanges = async () => {
    if (pendingAdditions.length === 0 && pendingRemovals.size === 0) {
      setAddDelegateOpen(false);
      return;
    }

    setSavingChanges(true);

    try {
      // Process removals first
      for (const spaceMemberId of pendingRemovals) {
        const delegate = delegates.find(d => d.space_member_id === spaceMemberId);
        if (delegate) {
          const result = await removeDelegateFromBooking({
            booking,
            bookingDelegate: delegate
          });
          if (!result.success) {
            console.error('Failed to remove delegate:', result.error);
          }
        }
      }

      // Process additions
      for (const member of pendingAdditions) {
        const memberForService = {
          id: member.id || member.space_member_id,
          user_id: member.user_id || member.user_profiles?.id,
          user_profiles: member.user_profiles || member.user
        };
        const result = await addDelegateToBooking({
          booking,
          spaceMember: memberForService,
          createdBy: userProfile?.id
        });
        if (!result.success) {
          console.error('Failed to add delegate:', result.error);
        }
      }

      // Refresh delegates from database
      await fetchDelegates();
      onDelegateChange?.();

      // Show success message
      const addedCount = pendingAdditions.length;
      const removedCount = pendingRemovals.size;
      if (addedCount > 0 && removedCount > 0) {
        toast.success(`Added ${addedCount} and removed ${removedCount} delegate(s)`);
      } else if (addedCount > 0) {
        toast.success(`Added ${addedCount} delegate(s)`);
      } else if (removedCount > 0) {
        toast.success(`Removed ${removedCount} delegate(s)`);
      }

      // Close dialog and reset
      resetPendingChanges();
      setAddDelegateOpen(false);
    } catch (error) {
      console.error('Error saving delegate changes:', error);
      toast.error('Failed to save changes');
    } finally {
      setSavingChanges(false);
    }
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = pendingAdditions.length > 0 || pendingRemovals.size > 0;

  const handleOpenRemoveConfirmation = (delegate) => {
    setSelectedDelegate(delegate);
    setConfirmRemoveOpen(true);
  };

  const handleRemoveDelegate = async () => {
    if (!selectedDelegate) return;

    setRemovingDelegate(true);
    try {
      const result = await removeDelegateFromBooking({
        booking,
        bookingDelegate: selectedDelegate
      });

      if (result.success) {
        toast.success('Delegate removed from booking');
        fetchDelegates();
        onDelegateChange?.();
      } else {
        toast.error('Failed to remove delegate', { description: result.error });
      }

      setConfirmRemoveOpen(false);
      setSelectedDelegate(null);
    } catch (error) {
      console.error('Error removing delegate:', error);
      toast.error('Failed to remove delegate');
    } finally {
      setRemovingDelegate(false);
    }
  };

  const handleOpenUploadDialog = (delegate) => {
    setSelectedDelegate(delegate);
    setUploadDialogOpen(true);
  };

  const handleOpenFailedConfirmation = (delegate) => {
    setSelectedDelegate(delegate);
    setConfirmFailedOpen(true);
  };

  const handleConfirmFailedProceed = () => {
    setConfirmFailedOpen(false);
    setFailedDialogOpen(true);
  };

  const handleUploadSuccess = () => {
    fetchDelegates();
  };

  const handleFailedSuccess = () => {
    fetchDelegates();
  };

  const handleDownloadCertificate = async (delegate) => {
    try {
      if (!delegate.certificate_url) {
        toast.error('No certificate available');
        return;
      }

      window.open(delegate.certificate_url, '_blank');
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error('Failed to download certificate');
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-3.5 w-3.5 ml-1 text-gray-400" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="h-3.5 w-3.5 ml-1" />
      : <ArrowDown className="h-3.5 w-3.5 ml-1" />;
  };

  // Filter and sort delegates
  const filteredAndSortedDelegates = useMemo(() => {
    let filtered = delegates;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(delegate => {
        const profile = delegate.space_member?.user_profiles;
        const name = profile?.display_name?.toLowerCase() || '';
        const email = profile?.email?.toLowerCase() || '';
        return name.includes(query) || email.includes(query);
      });
    }

    filtered = filtered.filter(delegate => {
      const attendanceMatch = filters[delegate.attendance_status];
      const completionMatch = filters[delegate.completion_status];
      return attendanceMatch || completionMatch;
    });

    const sorted = [...filtered].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'name':
          aValue = a.space_member?.user_profiles?.display_name || '';
          bValue = b.space_member?.user_profiles?.display_name || '';
          break;
        case 'email':
          aValue = a.space_member?.user_profiles?.email || '';
          bValue = b.space_member?.user_profiles?.email || '';
          break;
        case 'attendance':
          aValue = a.attendance_status || '';
          bValue = b.attendance_status || '';
          break;
        case 'completion':
          aValue = a.completion_status || '';
          bValue = b.completion_status || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [delegates, searchQuery, filters, sortColumn, sortDirection]);

  const getAttendanceBadge = (status) => {
    switch (status) {
      case 'attended':
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Attended
          </Badge>
        );
      case 'absent':
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
            <XCircle className="h-3 w-3 mr-1" />
            Absent
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
            <XCircle className="h-3 w-3 mr-1" />
            Cancelled
          </Badge>
        );
      case 'pending':
      default:
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
    }
  };

  const getCompletionBadge = (status, hasCertificate) => {
    switch (status) {
      case 'completed':
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <FileCheck className="h-3 w-3 mr-1" />
            {hasCertificate ? 'Certificate Uploaded' : 'Completed'}
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
            <XCircle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        );
      case 'not_started':
      default:
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            <Clock className="h-3 w-3 mr-1" />
            Not Started
          </Badge>
        );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter(v => !v).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="h-5 w-5" />
          Enrolled Delegates
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Empty state with add button */}
        {delegates.length === 0 ? (
          <div className="text-center py-12 text-gray-500 border border-dashed border-gray-200 rounded-lg">
            <Users className="h-10 w-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm font-medium">No delegates enrolled</p>
            <p className="text-xs text-gray-400 mt-1">Add delegates to track their attendance and completion</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => setAddDelegateOpen(true)}
            >
              <Users className="h-4 w-4 mr-2" />
              Manage Delegates
            </Button>
          </div>
        ) : (
          <>
            {/* Table Header with Search, Filters, and Add button */}
            <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search delegates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Filter Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Filter className="h-4 w-4" />
                  Filter
                  {getActiveFilterCount() > 0 && (
                    <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">
                      {getActiveFilterCount()}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Attendance Status</DropdownMenuLabel>
                <DropdownMenuCheckboxItem
                  checked={filters.pending}
                  onCheckedChange={(checked) => setFilters({ ...filters, pending: checked })}
                >
                  Pending
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={filters.attended}
                  onCheckedChange={(checked) => setFilters({ ...filters, attended: checked })}
                >
                  Attended
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={filters.absent}
                  onCheckedChange={(checked) => setFilters({ ...filters, absent: checked })}
                >
                  Absent
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={filters.cancelled}
                  onCheckedChange={(checked) => setFilters({ ...filters, cancelled: checked })}
                >
                  Cancelled
                </DropdownMenuCheckboxItem>

                <DropdownMenuSeparator />
                <DropdownMenuLabel>Completion Status</DropdownMenuLabel>
                <DropdownMenuCheckboxItem
                  checked={filters.not_started}
                  onCheckedChange={(checked) => setFilters({ ...filters, not_started: checked })}
                >
                  Not Started
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={filters.completed}
                  onCheckedChange={(checked) => setFilters({ ...filters, completed: checked })}
                >
                  Completed
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={filters.failed}
                  onCheckedChange={(checked) => setFilters({ ...filters, failed: checked })}
                >
                  Failed
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Manage Delegates Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddDelegateOpen(true)}
              className="gap-2"
            >
              <Users className="h-4 w-4" />
              Manage
            </Button>
          </div>

          {/* Results count and assignment status */}
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>Showing {filteredAndSortedDelegates.length} of {delegates.length} delegates</span>
            {totalSpaces > 0 && (
              <span className={cn(
                'text-xs font-medium',
                assignedCount >= totalSpaces ? 'text-green-600' : 'text-gray-500'
              )}>
                {assignedCount}/{totalSpaces} assigned
              </span>
            )}
          </div>

          {/* Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[300px]">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 -ml-2"
                      onClick={() => handleSort('name')}
                    >
                      Delegate
                      {getSortIcon('name')}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 -ml-2"
                      onClick={() => handleSort('attendance')}
                    >
                      Attendance
                      {getSortIcon('attendance')}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 -ml-2"
                      onClick={() => handleSort('completion')}
                    >
                      Status
                      {getSortIcon('completion')}
                    </Button>
                  </TableHead>
                  <TableHead>Certificate</TableHead>
                  <TableHead className="w-[50px]">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedDelegates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                      No delegates match your filters
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndSortedDelegates.map((delegate) => {
                    const profile = delegate.space_member?.user_profiles;
                    const hasCertificate = !!delegate.certificate_url;

                    return (
                      <TableRow key={delegate.id}>
                        {/* Delegate Info */}
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {profile?.avatar_url ? (
                              <Avatar className="h-9 w-9">
                                <img
                                  src={profile.avatar_url}
                                  alt={getDisplayName(profile) || 'Delegate'}
                                  className="h-full w-full object-cover"
                                />
                              </Avatar>
                            ) : (
                              <div className="h-9 w-9 rounded-full bg-purple-100 flex items-center justify-center">
                                <User className="h-4 w-4 text-purple-600" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">
                                {getDisplayName(profile) || 'Unknown Delegate'}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {profile?.email || ''}
                              </p>
                            </div>
                          </div>
                        </TableCell>

                        {/* Attendance Status */}
                        <TableCell>
                          {getAttendanceBadge(delegate.attendance_status)}
                        </TableCell>

                        {/* Completion Status */}
                        <TableCell>
                          {getCompletionBadge(delegate.completion_status, hasCertificate)}
                        </TableCell>

                        {/* Certificate Info */}
                        <TableCell>
                          {hasCertificate ? (
                            <div className="text-xs">
                              <p className="text-gray-600">
                                Issued: {formatDate(delegate.certificate_issue_date)}
                              </p>
                              {delegate.certificate_expiry_date && (
                                <p className="text-gray-500">
                                  Expires: {formatDate(delegate.certificate_expiry_date)}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">No certificate</span>
                          )}
                        </TableCell>

                        {/* Actions */}
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                              >
                                <MoreVertical className="h-4 w-4" />
                                <span className="sr-only">Open menu</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => navigate(`/profile/${delegate.space_member?.user_id}`)}
                              >
                                <ExternalLink className="h-4 w-4 mr-2" />
                                View Profile
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {!hasCertificate && delegate.completion_status !== 'failed' && (
                                <DropdownMenuItem
                                  onClick={() => handleOpenUploadDialog(delegate)}
                                >
                                  <Upload className="h-4 w-4 mr-2" />
                                  Upload Certificate
                                </DropdownMenuItem>
                              )}
                              {delegate.completion_status === 'not_started' && (
                                <>
                                  {!hasCertificate && delegate.completion_status !== 'failed' && (
                                    <DropdownMenuSeparator />
                                  )}
                                  <DropdownMenuItem
                                    onClick={() => handleOpenFailedConfirmation(delegate)}
                                    className="text-red-600 focus:text-red-600"
                                  >
                                    <XCircle className="h-4 w-4 mr-2" />
                                    Mark as Failed
                                  </DropdownMenuItem>
                                </>
                              )}
                              {hasCertificate && delegate.completion_status === 'completed' && (
                                <DropdownMenuItem
                                  onClick={() => handleDownloadCertificate(delegate)}
                                >
                                  <Download className="h-4 w-4 mr-2" />
                                  Download Certificate
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleOpenRemoveConfirmation(delegate)}
                                className="text-red-600 focus:text-red-600"
                              >
                                <UserMinus className="h-4 w-4 mr-2" />
                                Remove from Booking
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}
      </CardContent>

      {/* Manage Delegates Dialog */}
      <Dialog open={addDelegateOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-6xl">
          <DialogHeader>
            <DialogTitle>Manage Delegates</DialogTitle>
            <DialogDescription>
              Add or remove delegates for this booking. Changes are saved when you click Save.
            </DialogDescription>
          </DialogHeader>

          <DelegatePicker
            booking={booking}
            assignedDelegates={assignedForPicker}
            onAdd={handlePendingAdd}
            onRemove={handlePendingRemove}
          />

          {/* Pending changes indicator */}
          {hasUnsavedChanges && (
            <div className="text-sm text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
              {pendingAdditions.length > 0 && pendingRemovals.size > 0 ? (
                <span>
                  <span className="text-green-600 font-medium">+{pendingAdditions.length} to add</span>
                  {' • '}
                  <span className="text-red-600 font-medium">-{pendingRemovals.size} to remove</span>
                </span>
              ) : pendingAdditions.length > 0 ? (
                <span className="text-green-600 font-medium">+{pendingAdditions.length} delegate(s) to add</span>
              ) : (
                <span className="text-red-600 font-medium">-{pendingRemovals.size} delegate(s) to remove</span>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => handleDialogClose(false)}
              disabled={savingChanges}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveChanges}
              disabled={savingChanges || !hasUnsavedChanges}
            >
              {savingChanges && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {savingChanges ? 'Saving...' : hasUnsavedChanges ? 'Save Changes' : 'No Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Remove Dialog */}
      {selectedDelegate && (
        <Dialog open={confirmRemoveOpen} onOpenChange={setConfirmRemoveOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5" />
                Remove Delegate?
              </DialogTitle>
              <DialogDescription className="pt-2">
                Remove{' '}
                <strong className="text-gray-900">
                  {getDisplayName(selectedDelegate.space_member?.user_profiles) || 'this delegate'}
                </strong>{' '}
                from this booking?
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <div className="bg-red-50 rounded-lg p-4 border border-red-200 space-y-2">
                <p className="text-sm text-red-800">
                  This will remove the delegate from this booking and reset their training status in the matrix.
                </p>
                {selectedDelegate.certificate_url && (
                  <p className="text-sm text-red-800 font-medium">
                    This delegate has an uploaded certificate. The certificate file will be preserved but the booking record will be removed.
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setConfirmRemoveOpen(false)}
                disabled={removingDelegate}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleRemoveDelegate}
                disabled={removingDelegate}
              >
                {removingDelegate && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Remove Delegate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Certificate Upload Dialog */}
      {selectedDelegate && (
        <CertificateUploadDialog
          open={uploadDialogOpen}
          onOpenChange={setUploadDialogOpen}
          delegate={selectedDelegate}
          booking={booking}
          onSuccess={handleUploadSuccess}
        />
      )}

      {/* Confirm Mark Failed Dialog */}
      {selectedDelegate && (
        <Dialog open={confirmFailedOpen} onOpenChange={setConfirmFailedOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5" />
                Mark Delegate as Failed?
              </DialogTitle>
              <DialogDescription className="pt-2">
                Are you sure you want to mark{' '}
                <strong className="text-gray-900">
                  {getDisplayName(selectedDelegate.space_member?.user_profiles) || 'this delegate'}
                </strong>{' '}
                as failed for this training?
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <p className="text-sm text-red-800">
                  This action will mark the delegate as having attended but failed the training.
                  They will need to retake the course to become qualified.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setConfirmFailedOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmFailedProceed}
              >
                Yes, Mark as Failed
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Mark Failed Dialog (with notes) */}
      {selectedDelegate && (
        <MarkFailedDialog
          open={failedDialogOpen}
          onOpenChange={setFailedDialogOpen}
          delegate={selectedDelegate}
          booking={booking}
          onSuccess={handleFailedSuccess}
        />
      )}
    </Card>
  );
};

export default BookedDelegatesTable;
